import { z } from 'zod';
import { AuctionConfigSchema } from './auction';
import { DataSource } from './car-enums';
import { EngineTypeSchema, LotTypeSchema } from './ingest';
import { SourceListingSchema } from './source';
/**
 * Canonical Car contract — the single replacement for 6 independent Car
 * definitions across the platform.
 *
 * Field source of truth: cars-finder-api/app/models/alpicar_car.py:AlpicarCar
 * (the most complete definition, all fields covered 1:1 below, same order).
 *
 * Data-contract quirks FIXED AS TYPES (from api-node car.schemas.ts:12-14):
 *  - auction_end_date: BSON Date (z.coerce.date — an ISO string coerces, but a
 *    non-date string fails; a plain string in Mongo makes the car invisible to
 *    web auction filters)
 *  - odometer_reading: canonical write shape is the string "N km" (web splits
 *    on space); legacy scraper docs hold bare ints, so reads admit both.
 *    mileage_km is the numeric twin used by range filters.
 *  - current_bid: STRING, never a number (Python model + hermes mapper both
 *    String() it). The schema rejects numbers to keep writers honest.
 *  - multilingual fields: string | {en, pl?, ua} (28 fields, marked ML below)
 */
/** Accepts extra language keys some legacy docs carry (e.g. de/fr). */
export const MultilingualFieldSchema = z
    .object({
    en: z.string().nullish(),
    pl: z.string().nullish(),
    ua: z.string().nullish(),
})
    .passthrough();
export const LocalizedStringSchema = z.union([z.string(), MultilingualFieldSchema]);
/** available / roadworthy / service_book / accident: bool from the legacy scraper, ML from hermes. */
export const LocalizedFlagSchema = z.union([z.boolean(), LocalizedStringSchema]);
export const MultilingualListSchema = z
    .object({
    en: z.array(z.string()).nullish(),
    pl: z.array(z.string()).nullish(),
    ua: z.array(z.string()).nullish(),
})
    .passthrough();
export const LocalizedStringListSchema = z.union([z.array(z.string()), MultilingualListSchema]);
export const CarSchema = z
    .object({
    // ── Required identity ────────────────────────────────────────────────
    listing_id: z.string(),
    url: z.string(),
    data_source: z.nativeEnum(DataSource).default(DataSource.ALPICAR),
    // ── 🟦 Basic information ─────────────────────────────────────────────
    insurer: LocalizedStringSchema.nullish(), // ML
    make: z.string().nullish(),
    model: z.string().nullish(),
    year: z.number().int().nullish(),
    type: LocalizedStringSchema.nullish(), // ML
    /** car / motorcycle / bicycle / … — from the ingest `lotType` (normalizeLotType). */
    lot_type: LotTypeSchema.nullish(),
    odometer_reading: z.union([z.string(), z.number().int()]).nullish(), // quirk: "N km"
    mileage_km: z.number().int().nullish(),
    vin: z.string().nullish(),
    /** Phase 10: partner VIN-history link (carVertical affiliate); web derives it when absent. */
    vin_check_url: z.string().nullish(),
    registration_date: z.string().nullish(),
    name: LocalizedStringSchema.nullish(), // ML
    homologation_no: z.string().nullish(),
    brand: z.string().nullish(),
    reference_number: z.string().nullish(),
    claim_number: z.string().nullish(),
    stock_number: z.string().nullish(),
    status: LocalizedStringSchema.nullish(), // ML
    available: LocalizedFlagSchema.nullish(), // ML
    // ── 🟩 Engine & transmission ─────────────────────────────────────────
    fuel_type: LocalizedStringSchema.nullish(), // ML
    engine_power: z.string().nullish(), // "N kW" (hermes) / "N HP" (legacy)
    engine_displacement: z.string().nullish(), // "N cc"
    /** true when engine_displacement was derived from litres (2.0 → 2000 cc), not read as cm³. */
    engine_displacement_approx: z.boolean().nullish(),
    /**
     * petrol / diesel / hybrid / mhev / … — from the ingest `vehicle.engineType`.
     * NOT `engine_type`: that name is the legacy free-text field the landed-cost
     * engine still parses litres out of (deriveVehicleFromCar).
     */
    fuel_kind: EngineTypeSchema.nullish(),
    transmission: LocalizedStringSchema.nullish(), // ML
    gears: z.number().int().nullish(),
    drive: LocalizedStringSchema.nullish(), // ML
    roadworthy: LocalizedFlagSchema.nullish(), // ML
    // ── 🟨 Vehicle details ───────────────────────────────────────────────
    color: LocalizedStringSchema.nullish(), // ML
    interior_color: LocalizedStringSchema.nullish(), // ML
    condition: LocalizedStringSchema.nullish(), // ML
    tyre_profile: z.string().nullish(),
    unloaded_weight: z.number().int().nullish(),
    doors: z.number().int().nullish(),
    seats: z.number().int().nullish(),
    // ── 🟧 Documentation & compliance ────────────────────────────────────
    vehicle_registration: LocalizedStringSchema.nullish(), // ML
    certificate_of_conformity: LocalizedStringSchema.nullish(), // ML
    service_book: LocalizedFlagSchema.nullish(), // ML
    emissions_code: z.string().nullish(),
    last_inspection: z.string().nullish(),
    // ── 🟥 Damage & condition ────────────────────────────────────────────
    damage_description: LocalizedStringSchema.nullish(), // ML
    damage_location: LocalizedStringSchema.nullish(), // ML
    pre_damage: LocalizedStringSchema.nullish(), // ML
    usable_parts: LocalizedStringSchema.nullish(), // ML
    defects: LocalizedStringSchema.nullish(), // ML
    accident: LocalizedFlagSchema.nullish(), // ML
    // ── 🟪 Pricing ───────────────────────────────────────────────────────
    current_bid: z.string().nullish(), // quirk: string, CHF
    /**
     * Numeric mirror of `current_bid` (Phase 9 bidding v2). Backfilled by the
     * add-current-bid-value migration; the atomic bid-accept step conditions
     * and updates this field, keeping the string twin in sync for parity.
     */
    current_bid_value: z.number().nullish(),
    /** userId of the current internal leader — set by the atomic accept step. */
    current_bid_user_id: z.string().nullish(),
    /**
     * Phase 15 (upstream-higher-bid): the highest price the relay desk has SEEN
     * on the real auction for this lot (CHF). Only ever rises (`$max`). The
     * counter never re-resolves below it: a withdrawal cannot promote a bid at
     * or under it, and with no eligible bid left the counter returns to it
     * (held by `UPSTREAM_LEADER_ID`) instead of being cleared.
     */
    upstream_price_floor: z.number().nullish(),
    current_value: z.number().nullish(),
    catalog_price: z.number().nullish(),
    repair_costs: z.number().nullish(),
    equipment_price: z.number().nullish(),
    accessories_price: z.number().nullish(),
    // ── ⚙️ Equipment ─────────────────────────────────────────────────────
    standard_equipment: LocalizedStringListSchema.nullish(), // ML list
    special_equipment: LocalizedStringListSchema.nullish(), // ML list
    important_information: LocalizedStringSchema.nullish(), // ML
    // ── 📦 Logistics ─────────────────────────────────────────────────────
    pickup_at_location: LocalizedStringSchema.nullish(), // ML
    transport_via: LocalizedStringSchema.nullish(), // ML
    location: LocalizedStringSchema.nullish(), // ML
    // ── 📸 Images ────────────────────────────────────────────────────────
    images: z.array(z.string()).nullish(),
    main_image: z.string().nullish(),
    thumbnail_image: z.string().nullish(),
    // ── 🕐 Metadata ──────────────────────────────────────────────────────
    created_at: z.coerce.date().nullish(),
    /** Last write by ANY scraper path (full scrape, countdown re-scrape, hermes ingest). */
    updated_at: z.coerce.date().nullish(),
    /** Countdown re-scrape only (services/scraper countdown_update_service.py). */
    last_rescrape_at: z.coerce.date().nullish(),
    scraped_at: z.coerce.date().nullish(),
    auction_countdown: z.string().nullish(), // "HH:MM:SS" or "1 day"
    auction_end_date: z.coerce.date().nullish(), // quirk: BSON Date
    auction_name: z.string().nullish(), // web builds the auction dropdown from this
    /** Bidding v2 rules for this lot (partial; resolveAuctionConfig applies defaults → blind_mirror). */
    auction_config: AuctionConfigSchema.partial().nullish(),
    /** Soft-close extensions already applied (open_proxy; capped by maxAutoExtensions). */
    auction_extensions_count: z.number().int().nullish(),
    // ── 🌐 Multi-source (Phase 15 — source abstraction) ──────────────────
    /**
     * Free-form per-source metadata bag (e.g. WOM lot number, autobid seller
     * id). Additive and optional; never overwrites a modeled field. The ingest
     * payload's `extra` block (homologation no., eco code, COC, weight, …) lands here.
     */
    source_meta: z.record(z.unknown()).nullish(),
    /**
     * Every source that has ever ingested this VIN, including the winner. The
     * winning source (highest `sources.priority`) owns the core fields above;
     * the losers survive here so the UI can surface cross-listings. Built by
     * the ingest VIN-merge policy (ingest.service.ts).
     */
    source_listings: z.array(SourceListingSchema).nullish(),
    // ── 📊 Additional ────────────────────────────────────────────────────
    raw_data: z.record(z.unknown()).nullish(),
    notes: z.string().nullish(),
    // ── 🔎 Search (smart lot search) ─────────────────────────────────────
    // Derived by buildSearchDoc (search.ts) — never sent to clients.
    /** Normalised full text of the lot (≤ 16 000 chars). */
    search_text: z.string().optional(),
    /** Sorted dictionary tags, e.g. 'fuel:diesel', 'brand:bmw'. */
    search_tags: z.array(z.string()).optional(),
    /** SEARCH_DOC_VERSION the two fields above were built with. */
    search_v: z.number().int().optional(),
})
    // Shared collections are strict:false in every consumer — unmodeled fields
    // (e.g. Mongo _id, ai_* projections) must survive round-trips.
    .passthrough();
/** Total number of modeled fields — frozen by contract tests. */
export const CAR_SCHEMA_FIELD_COUNT = Object.keys(CarSchema.shape).length;
/*
 * REMOVED 2026-09-21 — the phase-13 lot-freshness helpers (`STALE_LOT_HOURS`,
 * `LOT_FRESHNESS_FIELDS`, `lotDataAsOf`, `isLotDataStale`).
 *
 * They existed for ONE consumer: the auction bid panel disabled its CTA when a
 * lot's last scraper stamp was over six hours old. Lots are scraped once rather
 * than on a cycle, so that gate refused 100 % of bids permanently (596 of 596
 * lots stale when it was measured), and it was removed with its only caller. The
 * `updated_at` / `last_rescrape_at` / `scraped_at` fields themselves stay on the
 * contract and are still written by `countdown-update.service.ts` — only the
 * derived staleness rule is gone. Git history has the implementation if a
 * recurring rescrape ever makes it meaningful again.
 */
/** The 28 multilingual fields (25 LocalizedString/Flag + 2 lists + name). */
export const CAR_MULTILINGUAL_FIELDS = [
    'insurer',
    'type',
    'name',
    'status',
    'available',
    'fuel_type',
    'transmission',
    'drive',
    'roadworthy',
    'color',
    'interior_color',
    'condition',
    'vehicle_registration',
    'certificate_of_conformity',
    'service_book',
    'damage_description',
    'damage_location',
    'pre_damage',
    'usable_parts',
    'defects',
    'accident',
    'important_information',
    'pickup_at_location',
    'transport_via',
    'location',
    'standard_equipment',
    'special_equipment',
];
//# sourceMappingURL=car.js.map